import React, { Component } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck'
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes'
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons/faSpinnerThird'
import PrimaryButton from '@softwaredev_cegeka/stix-core/dist/components/Button/subcomponents/PrimaryButton'
import Input from '@softwaredev_cegeka/stix-form/dist/components/Input'

import RadioCheckmark from './RadioCheckmark'
import styles from './impersonationOptionSelector.module.scss'

class OptionSelector extends Component {
    static propTypes = {
        options: PropTypes.arrayOf(PropTypes.object).isRequired,
        onSearch: PropTypes.func.isRequired,
        onApplySelection: PropTypes.func.isRequired,
        onRemoveSelection: PropTypes.func.isRequired,
        searchLabel: PropTypes.string.isRequired,
        searchPlaceholder: PropTypes.string,
        selectedOption: PropTypes.object,
        searchKey: PropTypes.oneOf(['id', 'username']),
    }

    static defaultProps = {
        searchKey: 'id',
    }

    state = {
        currentSelectedItemId: null,
        inputSearchValue: '',
        loadingAppplySelection: false,
        loadingRemoveSelection: false,
        loadingSearch: false,
    }

    async componentDidMount() {
        const { selectedOption } = this.props

        // Make an initial search on component mount
        this.triggerOnSearch('')

        if (selectedOption) {
            this.setState({ currentSelectedItemId: selectedOption.id })
        }
    }

    /**
     *  Debounce [triggerOnSearch] function
     */
    debouncedCall = debounce(async searchValue => {
        this.triggerOnSearch(searchValue)
    }, 350)

    /**
     * Trigger [onSearch] prop
     *
     * @param {String} searchValue
     *
     */
    triggerOnSearch = async searchValue => {
        const { onSearch } = this.props

        try {
            this.setState({ loadingSearch: true })
            await onSearch(searchValue)
            this.setState({ loadingSearch: false })
        } catch (error) {
            console.error(error)
            this.setState({ loadingSearch: false })
        }
    }

    /**
     * Handle input search using the [debouncedCall]
     *
     * @param {React.FormEvent<HTMLInputElement>} event
     */
    handleDebouncedSearch = event => {
        const searchValue = event.target.value

        this.setState({ inputSearchValue: searchValue })
        this.debouncedCall(searchValue)
    }

    /**
     * Handle apply selection of the selected item
     *
     * @param {React.FormEvent<HTMLInputElement>} event
     */
    handleApplySelection = async () => {
        const { currentSelectedItemId } = this.state
        const { options, onApplySelection, onSearch } = this.props

        this.setState({ loadingAppplySelection: true })
        await onApplySelection(
            options.find(item => item.id === currentSelectedItemId)
        )
        await onSearch('')
        this.setState({ loadingAppplySelection: false, inputSearchValue: '' })
    }

    /**
     * Remove selection of the selected item
     */
    handleRemoveSelection = async () => {
        const { onRemoveSelection, onSearch } = this.props

        this.setState({
            currentSelectedItemId: null,
            loadingRemoveSelection: true,
        })
        await onRemoveSelection()
        await onSearch('')
        this.setState({
            currentSelectedItemId: null,
            inputSearchValue: '',
            loadingRemoveSelection: false,
        })
    }

    /**
     * Handle option change
     *
     * @param {React.FormEvent<HTMLInputElement>} event
     */
    handleOnChange = event => {
        this.setState({ currentSelectedItemId: event.target.value })
    }

    render() {
        const {
            currentSelectedItemId,
            loadingAppplySelection,
            loadingRemoveSelection,
            loadingSearch,
            inputSearchValue,
        } = this.state
        const {
            options,
            searchLabel,
            searchPlaceholder,
            selectedOption,
            searchKey,
        } = this.props
        if (selectedOption) {
            const selectedItemIndex = options.findIndex(
                item => item[searchKey] === selectedOption[searchKey]
            )

            // Move selected item at the top of the list
            if (selectedItemIndex > -1) {
                const optionToBeMoved = options[selectedItemIndex]
                options.splice(selectedItemIndex, 1)
                options.unshift(optionToBeMoved)
                selectedItemIndex !== 0 &&
                    this.setState({
                        currentSelectedItemId: options[0].id,
                    })
            } else {
                // If the current list does not contain the selected item (because it was selected
                // some other time) also add it at the top of the list
                options.unshift(selectedOption)
            }
        }

        return (
            <form>
                <div className={styles.searchInputFormGroup}>
                    <Input
                        endIcon={
                            loadingSearch ? (
                                <FontAwesomeIcon
                                    style={{ marginRight: '1rem' }}
                                    icon={faSpinnerThird}
                                    spin
                                />
                            ) : undefined
                        }
                        disabled={
                            loadingAppplySelection || loadingRemoveSelection
                        }
                        label={searchLabel}
                        onChange={this.handleDebouncedSearch}
                        placeholder={searchPlaceholder}
                        srOnly
                        startIcon={<FontAwesomeIcon icon={faSearch} />}
                        value={inputSearchValue}
                        rounded={true}
                    />
                </div>

                <div className={styles.optionsContainer}>
                    {options.map(item => (
                        <RadioCheckmark
                            checked={currentSelectedItemId === item.id}
                            key={item.id ?? item.username}
                            label={item.label}
                            loading={loadingRemoveSelection}
                            name="users"
                            onChange={this.handleOnChange}
                            onSecondaryAction={this.handleRemoveSelection}
                            secondaryActionIcon={
                                selectedOption &&
                                selectedOption[searchKey] === item[searchKey]
                                    ? loadingRemoveSelection
                                        ? faSpinnerThird
                                        : faTimes
                                    : null
                            }
                            value={item.id}
                        />
                    ))}
                </div>

                {currentSelectedItemId ? (
                    <div className={styles.applySelectionContainer}>
                        <PrimaryButton
                            block
                            disabled={
                                !currentSelectedItemId ||
                                loadingAppplySelection ||
                                loadingRemoveSelection
                            }
                            onClick={this.handleApplySelection}
                            startIcon={
                                <FontAwesomeIcon
                                    icon={
                                        loadingAppplySelection
                                            ? faSpinnerThird
                                            : faCheck
                                    }
                                    spin={loadingAppplySelection}
                                />
                            }
                        >
                            {loadingAppplySelection
                                ? 'Applying selection...'
                                : 'Apply selection'}
                        </PrimaryButton>
                    </div>
                ) : null}
            </form>
        )
    }
}

export default OptionSelector
